// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.

import 'bootstrap'
import ScrollSpy from 'bootstrap/js/dist/scrollspy'
import { options } from './helper'
import { highlight } from './highlight'
import { renderMarkdown } from './markdown'
import { enableSearch } from './search'
import { renderToc } from './toc'
import { initTheme } from './theme'
import { renderBreadcrumb, renderInThisArticle, renderNavbar } from './nav'
import { initLanguagePicker, renderTranslationWarning, applyUiStringTranslations } from './language'
import { headerSearch } from './hubspot/modules/site-header'
import 'bootstrap-icons/font/bootstrap-icons.scss'
import './docfx.scss'
import './local.scss'
import SimpleLightbox from 'simplelightbox'

declare global {
  interface Window {
    docfx: {
      ready?: boolean,
      searchReady?: boolean,
      searchResultReady?: boolean,
    }
  }
}

async function init() {
  window.docfx = window.docfx || {}

  const { start } = await options()
  start?.()

  const pdfmode = navigator.userAgent.indexOf('docfx/pdf') >= 0
  if (pdfmode) {
    await Promise.all([
      renderMarkdown(),
      highlight()
    ])
  } else {
    // Apply UI string translations first so loc:* meta overrides are in place
    // before renderInThisArticle() calls loc('inThisArticle')
    await applyUiStringTranslations()

    await Promise.all([
      initTheme(),
      enableSearch(),
      renderInThisArticle(),
      renderMarkdown(),
      renderNav(),
      highlight(),
      initLanguagePicker(),
      renderTranslationWarning()
    ])
  }

  window.docfx.ready = true

  async function renderNav() {
    const [navbar, toc] = await Promise.all([renderNavbar(), renderToc()])
    renderBreadcrumb([...navbar, ...toc])
  }
  headerSearch()

  // Add lightbox to the <a> tags that have <img> as a child in article.
  const lightboxOptions = {}
  function initializeLightbox(element: Element, options: object) {
    return new SimpleLightbox(element, options)
  }
  document.querySelectorAll('article a').forEach(a => {
    if (a.querySelector('img')) {
      initializeLightbox(a, lightboxOptions)
    }
  })

  // Track scroll position and direction
  let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop
  // Default direction
  let scrollDirection = 'down'
  // Margin for scrolling down, add top 80% to make longer sections active without any visible section headers.
  // Need to decrease bottom to make it possible to active the section earlier, example for the initial load.
  // Makes it a bit more inaccurate, especially on click, but it's otherwise sections won't keep active when really long.
  const scrollDownRootMargin = '+80% 0px -10%'
  // Margin for scrolling up, need to decrease top when scrolling up to make the section active earlier.
  // Margin for scrolling up, add bottom 80% to make longer sections active without any visible section headers.
  const scrollUpRootMargin = '0px 0px +80%'
  // Use different root margins for different scroll directions.
  const useDifferentRootMargins = true

  // Function to update ScrollSpy based on scroll direction.
  const updateScrollSpy = (() => {
    let ticking = false

    return function() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollSpyContentEl = document.getElementById('article')
          if (!scrollSpyContentEl) return

          // Determine scroll direction.
          const st = window.pageYOffset || document.documentElement.scrollTop
          const newDirection = st > lastScrollTop ? 'down' : 'up'
          // Only update ScrollSpy if direction changed.
          if (newDirection !== scrollDirection) {
            scrollDirection = newDirection

            // Dispose existing instance.
            ScrollSpy.getInstance(scrollSpyContentEl)?.dispose()

            // Create new instance with direction-specific settings.
            const scrollSpy = ScrollSpy.getOrCreateInstance(scrollSpyContentEl, {
              target: '#affix',
              smoothScroll: false,
              rootMargin: scrollDirection === 'down'
                ? scrollDownRootMargin
                : scrollUpRootMargin
            })
            // console.log('ScrollSpy updated with direction:', scrollDirection, scrollSpy._config.rootMargin)
            scrollSpy.refresh()
          }

          // For mobile or negative scrolling.
          lastScrollTop = st <= 0 ? 0 : st
          ticking = false
        })

        ticking = true
      }
    }
  })()

  // Add scroll event listener with passive flag for better performance.
  if (useDifferentRootMargins) {
    window.addEventListener('scroll', updateScrollSpy, { passive: true })
  }

  const scrollSpyContentEl = document.getElementById('article')
  const scrollSpy = ScrollSpy.getOrCreateInstance(scrollSpyContentEl, {
    target: '#affix',
    smoothScroll: false,
    rootMargin: scrollDownRootMargin
  })
  scrollSpy.refresh()
}

init().catch(console.error)
